import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type MultistepStepConfig = {
  title: string;
  Component: React.ComponentType;
  id: string;
};

type MultistepContextValue<Data, ResultData> = {
  steps: MultistepStepConfig[];
  setSteps: React.Dispatch<React.SetStateAction<MultistepStepConfig[]>>;
  currentStepIndex: number;
  data: Data;
  setData: React.Dispatch<React.SetStateAction<Data>>;
  nextStep: () => void;
  previousStep: () => void;
  navToStepId: (id: string) => void;
  hasNext: boolean;
  hasPrevious: boolean;
  onComplete: (resultData: ResultData) => void;
  reset: () => void;
};

const MultistepContext = createContext<MultistepContextValue<any, any>>({
  steps: [],
  setSteps: () => {},
  data: {},
  setData: () => {},
  currentStepIndex: 0,
  nextStep: () => {},
  previousStep: () => {},
  navToStepId: () => {},
  hasNext: false,
  hasPrevious: false,
  onComplete: () => {},
  reset: () => {},
});

export function useMultistepContext<Data, ResultData = Data>() {
  const context = useContext(MultistepContext);
  if (!context) {
    throw new Error(
      "useMultistepContext must be used within a MultistepProvider",
    );
  }
  return context as MultistepContextValue<Data, ResultData>;
}

export type MultistepControlRefValue = {
  nextStep: () => void;
  previousStep: () => void;
  navToStepId: (id: string) => void;
};

export type MultistepControlRef =
  React.MutableRefObject<MultistepControlRefValue | null>;

type MultistepProviderProps<Data, ResultData> = PropsWithChildren<{
  steps: MultistepStepConfig[];
  initialData: Data;
  onChangeData?: (data: Data) => void;
  onComplete?: (result: ResultData) => void;
  controlRef?: MultistepControlRef;
}>;

export function MultistepProvider<Data, ResultData>({
  initialData,
  steps: initialSteps,
  children,
  onChangeData = () => {},
  onComplete: _onComplete = () => {},
  controlRef,
}: MultistepProviderProps<Data, ResultData>) {
  const [steps, setSteps] = useState<MultistepStepConfig[]>(initialSteps);
  const [data, setData] = useState<Data>(initialData);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  const handleDataChange = useCallback(
    (data: Data) => {
      setData(data);
      onChangeData(data);
    },
    [setData, onChangeData],
  );

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prevIndex) => {
      return Math.min(prevIndex + 1, steps.length - 1);
    });
  }, [steps.length]);

  const previousStep = useCallback(() => {
    setCurrentStepIndex((prevIndex) => {
      return Math.max(prevIndex - 1, 0);
    });
  }, []);

  const navToStepId = useCallback(
    (id: string) => {
      const stepIndex = steps.findIndex((step) => step.id === id);
      if (stepIndex !== -1) {
        setCurrentStepIndex(stepIndex);
      } else {
        console.warn(`Step with id "${id}" not found.`);
      }
    },
    [steps],
  );

  useEffect(() => {
    if (controlRef) {
      controlRef.current = {
        nextStep,
        previousStep,
        navToStepId,
      };
    }
  }, [controlRef, nextStep, previousStep, navToStepId]);

  const hasNext = currentStepIndex < steps.length - 1;
  const hasPrevious = currentStepIndex > 0;

  const reset = useCallback(() => {
    handleDataChange(initialData); // Reset data to initial state
    setCurrentStepIndex(0); // Reset to the first step
  }, [initialData, handleDataChange]);

  const onComplete = useCallback(
    (result: ResultData) => {
      _onComplete(result);
      reset();
    },
    [_onComplete, reset],
  );

  useEffect(() => {
    if (initialData) {
      setData(initialData);
    }
  }, [initialData]);

  return (
    <MultistepContext.Provider
      value={{
        steps,
        setSteps,
        currentStepIndex,
        data,
        setData: handleDataChange,
        nextStep,
        previousStep,
        navToStepId,
        hasNext,
        hasPrevious,
        onComplete,
        reset,
      }}
    >
      {children}
    </MultistepContext.Provider>
  );
}
